// apps/nos/client/src/hooks/useChromatogramUpload.ts
import { useCallback, useRef } from "react";
import {
  completeUpload,
  deleteUpload,
  presignUpload,
  putFileToS3,
  useAuthHeaders,
} from "@/services/uploads_api";
import type { Chroma, ChromaFormat } from "@/utils/types/upload";

function deriveFormat(filename: string): ChromaFormat {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".raw")) return "raw";
  if (lower.endsWith(".mzml")) return "mzML";
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".xlsx")) return "xlsx";
  throw new Error(`unsupported chromatogram extension: ${filename}`);
}

function deriveContentType(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".xlsx")) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  // .raw and .mzML are vendor binary — use generic octet-stream
  return "application/octet-stream";
}

export type StartUploadArgs = {
  draftId: string;
  file: File;
  machine: "agilent" | "thermo";
  // The hook does not own state — it returns a Chroma snapshot at each transition
  // and lets the caller (ChromatogramMode) reconcile into its `chromas` array.
  onTransition: (next: Chroma) => void;
};

export function useChromatogramUpload() {
  const getHeaders = useAuthHeaders();

  // Live AbortControllers, keyed by both the temp id and (after presign) the
  // real file_upload_id so the caller can cancel by whichever id their UI is
  // currently showing.
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const cancel = useCallback((fileUploadId: string): boolean => {
    const ctrl = controllersRef.current.get(fileUploadId);
    if (!ctrl) return false;
    ctrl.abort();
    // Don't delete from the map here — the start() error-handling path needs
    // to read controller.signal.aborted to distinguish cancel from genuine
    // failures. The successful/failed paths clean up at the end.
    return true;
  }, []);

  const start = useCallback(
    async (args: StartUploadArgs): Promise<void> => {
      const { draftId, file, machine, onTransition } = args;

      const tempId = `tmp-${crypto.randomUUID()}`;
      const controller = new AbortController();
      controllersRef.current.set(tempId, controller);

      const cleanup = (realId?: string) => {
        controllersRef.current.delete(tempId);
        if (realId) controllersRef.current.delete(realId);
      };

      // The hook owns server-side cleanup for cancelled in-flight uploads: it's
      // the only place that knows the real file_upload_id AND the phase. presign
      // may have already created the row (and /complete may never run), so on any
      // post-presign abort we delete the row we created. Without this, a cancel
      // during presign/upload orphans a server row the client can never see
      // (it never surfaced the real id).
      const abandonOnCancel = async (realId: string) => {
        try {
          const headers = await getHeaders();
          await deleteUpload(realId, headers);
        } catch {
          // Best-effort; finalize reconciles any straggler server-side.
        }
      };

      const format = deriveFormat(file.name);
      const contentType = deriveContentType(file.name);

      let snapshot: Chroma = {
        fileUploadId: tempId,
        filename: file.name,
        format,
        sizeBytes: file.size,
        contentType,
        machine,
        status: "presigning",
        progressPct: 0,
      };
      onTransition(snapshot);

      let presigned;
      try {
        const headers = await getHeaders();
        // NOTE: presign is deliberately NOT given controller.signal. It's a
        // cheap call and we need its response (the real file_upload_id) even if
        // the user cancels mid-flight — otherwise we'd create a server row we can
        // never identify to delete. The abort only cancels the big S3 PUT below.
        presigned = await presignUpload(
          {
            draft_id: draftId,
            kind: "chromatogram",
            filename: file.name,
            content_type: contentType,
            size_bytes: file.size,
            metadata: { machine },
          },
          headers,
        );
      } catch (err) {
        if (controller.signal.aborted) {
          // Cancelled, and presign also errored (e.g. network) — the row was
          // likely never created and we have no id to delete. Drop the
          // controller; the picker already removed the row optimistically.
          cleanup();
          return;
        }
        snapshot = {
          ...snapshot,
          status: "failed",
          errorMessage: err instanceof Error ? err.message : "presign failed",
        };
        onTransition(snapshot);
        cleanup();
        return;
      }

      // Track under the real id too so the caller can cancel after the swap.
      controllersRef.current.set(presigned.file_upload_id, controller);

      // Cancelled while presign was in flight? We now know the real id, so delete
      // the row we just created instead of proceeding to upload. The picker
      // already removed this file optimistically — don't transition.
      if (controller.signal.aborted) {
        await abandonOnCancel(presigned.file_upload_id);
        cleanup(presigned.file_upload_id);
        return;
      }

      // Swap temp id for real one.
      snapshot = {
        ...snapshot,
        fileUploadId: presigned.file_upload_id,
        status: "uploading",
        progressPct: 0,
      };
      onTransition(snapshot);

      try {
        await putFileToS3({
          url: presigned.presigned_url,
          file,
          requiredHeaders: presigned.required_headers,
          signal: controller.signal,
          onProgress: (pct) => {
            snapshot = { ...snapshot, progressPct: pct };
            onTransition(snapshot);
          },
        });
      } catch (err) {
        if (controller.signal.aborted) {
          await abandonOnCancel(presigned.file_upload_id);
          cleanup(presigned.file_upload_id);
          return;
        }
        snapshot = {
          ...snapshot,
          status: "failed",
          errorMessage: err instanceof Error ? err.message : "upload failed",
        };
        onTransition(snapshot);
        cleanup(presigned.file_upload_id);
        return;
      }

      snapshot = { ...snapshot, status: "verifying" };
      onTransition(snapshot);

      try {
        const headers = await getHeaders();
        await completeUpload(presigned.file_upload_id, headers, controller.signal);
      } catch (err) {
        if (controller.signal.aborted) {
          await abandonOnCancel(presigned.file_upload_id);
          cleanup(presigned.file_upload_id);
          return;
        }
        snapshot = {
          ...snapshot,
          status: "failed",
          errorMessage:
            err instanceof Error ? err.message : "verification failed",
        };
        onTransition(snapshot);
        cleanup(presigned.file_upload_id);
        return;
      }

      snapshot = { ...snapshot, status: "uploaded", progressPct: 100 };
      onTransition(snapshot);
      cleanup(presigned.file_upload_id);
    },
    [getHeaders],
  );

  return { start, cancel };
}
